import { BadgeEuro, Check, Scale } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { EmptyState } from "../components/EmptyState";
import { trpc } from "../lib/trpc";
import { MATERIAL_UNIT_COMPACT_LABELS } from "../../shared/material-units";

const shortUnits = MATERIAL_UNIT_COMPACT_LABELS;

function formatEuros(cents: number | string) {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(cents) / 100);
}

export function PriceComparisonPage() {
  const materials = trpc.materials.list.useQuery();
  const [materialId, setMaterialId] = useState("");
  const selectedMaterialId = materialId || materials.data?.[0]?.id || "";
  const offers = trpc.materials.compareOffers.useQuery(
    { materialId: selectedMaterialId },
    { enabled: Boolean(selectedMaterialId) },
  );
  const selectedMaterial = useMemo(
    () => materials.data?.find((material) => material.id === selectedMaterialId),
    [materials.data, selectedMaterialId],
  );
  const queryError = materials.error ?? offers.error;
  const queryLoading = materials.isLoading || offers.isLoading;

  useEffect(() => {
    if (!materialId && materials.data?.[0]) setMaterialId(materials.data[0].id);
  }, [materialId, materials.data]);

  return (
    <section className="purchase-page">
      <header className="page-heading">
        <div><p className="eyebrow">Decisione d'acquisto</p><h1>Confronto prezzi</h1><p>Confronta confezioni diverse usando il costo reale per unità.</p></div>
      </header>

      <div className="comparison-selector">
        <label><span>Materiale da confrontare</span><select value={selectedMaterialId} onChange={(event) => setMaterialId(event.target.value)}>{materials.data?.map((material) => <option key={material.id} value={material.id}>{material.name}</option>)}</select></label>
        {selectedMaterial && <div><Scale size={20} /><span>Unità comune<strong>{shortUnits[selectedMaterial.baseUnit]}</strong></span></div>}
      </div>

      {queryError && <div className="query-error" role="alert"><strong>Confronto non disponibile</strong><span>{queryError.message}</span><button className="button button--outline" type="button" onClick={() => void Promise.all([materials.refetch(), offers.refetch()])}>Riprova</button></div>}
      {!queryError && queryLoading && <p className="loading-copy">Caricamento confronto prezzi...</p>}

      {!queryError && !queryLoading && <div className="comparison-list">
        {offers.data?.map((offer, index) => (
          <article className={`comparison-card${index === 0 ? " comparison-card--best" : ""}`} key={offer.id}>
            <span className="comparison-card__rank">{index + 1}</span>
            <div className="comparison-card__supplier"><h2>{offer.supplierName}</h2><p>{offer.brandVariant || "Marca non indicata"}</p></div>
            <div className="comparison-card__package"><strong>{offer.packageDescription}</strong><span>{formatEuros(offer.packagePriceCents)} a confezione</span><small>Aggiornato il {new Intl.DateTimeFormat("it-IT", { dateStyle: "long" }).format(new Date(offer.updatedAt))}</small></div>
            <div className="comparison-card__price"><BadgeEuro size={21} /><span><strong>{formatEuros(offer.unitPriceCents)} / {shortUnits[offer.packageUnit]}</strong><small>prezzo normalizzato</small></span></div>
            {index === 0 && <span className="best-chip"><Check size={14} /> Più conveniente</span>}
          </article>
        ))}
      </div>}
      {!queryError && !queryLoading && selectedMaterialId && !offers.data?.length && <EmptyState title="Nessun prezzo confrontabile">Inserisci almeno un prezzo nella sezione Fornitori.</EmptyState>}
      {!queryError && !queryLoading && !selectedMaterialId && <EmptyState title="Catalogo vuoto">Crea prima un materiale nella sezione Fornitori.</EmptyState>}
    </section>
  );
}
